import type { ChannelClient } from '@hibro/platform-core';
import type {
  ActiveGate,
  IModuleService,
  ResolvedContributions,
  ResolvedView,
} from '@hibro/shared';
import { CONTRIBUTIONS_CHANGED_EVENT } from '@hibro/shared';
import { UiClientConfigProvider } from '@hibro/ui-client';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  CustomHeader,
  type WindowControlsBridge,
} from '../components/custom-header/custom-header';
import {
  ViewEventBusContext,
  type ViewEventSubscribe,
} from '../event-bus/event-bus';
import {
  NavigationContext,
  type NavigationSource,
} from '../navigation/navigation';
import { type LoadUi, UiRegistry } from '../runtime/ui-registry';
import { resolveOverlayPortalContainer } from '../theme/portal-container';
import type { IframeBridge } from './iframe-surface';
import { OverlayProvider } from './overlay-host';
import { SurfaceView } from './surface-host';
import { SurfaceHostedInProvider } from './surface-hosted-in';

export interface GateHostProps {
  /** The active application gate to present full-screen (架构方案 §5.8 App Gate). */
  gate: ActiveGate;
  /** Auto RPC proxy for the host module service (shared instance, §4.3). */
  moduleService: IModuleService;
  /** Lazy UI importers keyed by module id, discovered in the app shell. */
  uiLoaders: Map<string, LoadUi>;
  /** Channel client for EventBus subscriptions (view `onEvent` + hot re-resolve). */
  client?: ChannelClient;
  /** Bridge for in-DOM `iframe` gate views (见 9; injected by the shell). */
  iframeBridge?: IframeBridge;
  /** Source of `navigate(view:<id>, …)` locations (见 8); injected by the shell. */
  navigation?: NavigationSource;
  /** Custom title-bar window controls bridge (§5.8); injected by the shell. */
  windowControlsBridge?: WindowControlsBridge;
}

/**
 * Full-screen host for the application root gate (架构方案 §5.8 App Gate). A third
 * presentation dimension orthogonal to layout areas and OverlaySurface: it renders
 * the gate module's view *in place of* the workbench (the `Workbench` is never
 * mounted), so pre-gate the user cannot see or enter it. The kernel stays
 * policy-free — a `source` module raises/releases the gate via `context.gate`.
 *
 * Mirrors {@link DetachedSurface} (a minimal single-surface shell): it resolves
 * the gate view's real `ResolvedView` from `listContributions`, builds a
 * `UiRegistry` over the full view set (so overlays a gate stage opens can resolve
 * too), and renders it through {@link SurfaceView}. Keeps the frameless chrome
 * (drag region + window controls) so the window is movable/closable pre-login.
 */
export function GateHost({
  gate,
  moduleService,
  uiLoaders,
  client,
  iframeBridge,
  navigation,
  windowControlsBridge,
}: GateHostProps): ReactNode {
  const [views, setViews] = useState<ResolvedView[]>([]);
  const [runtimeUiEntries, setRuntimeUiEntries] = useState<
    ResolvedContributions['runtimeUiEntries']
  >([]);

  const refresh = useCallback(() => {
    let cancelled = false;
    void moduleService
      .listContributions()
      .then((contributions) => {
        if (!cancelled) {
          setViews(contributions.views);
          setRuntimeUiEntries(contributions.runtimeUiEntries);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [moduleService]);

  useEffect(() => refresh(), [refresh]);

  // Re-resolve when the active module set changes at runtime (见 12) — e.g. the
  // gate-owning module is hot-installed after the shell booted.
  useEffect(() => {
    if (!client) {
      return;
    }
    return client.listen(CONTRIBUTIONS_CHANGED_EVENT, () => {
      refresh();
    });
  }, [client, refresh]);

  const view = useMemo(
    () => views.find((v) => v.id === gate.viewId) ?? null,
    [views, gate.viewId],
  );

  const registry = useMemo(
    () => new UiRegistry(uiLoaders, views, undefined, runtimeUiEntries),
    [uiLoaders, views, runtimeUiEntries],
  );

  const onEvent = useMemo<ViewEventSubscribe>(
    () =>
      client
        ? (event, handler) => client.listen(event, handler)
        : () => () => undefined,
    [client],
  );

  return (
    <SurfaceHostedInProvider value="workbench">
      <UiClientConfigProvider portalContainer={resolveOverlayPortalContainer()}>
        <ViewEventBusContext.Provider value={onEvent}>
          <NavigationContext.Provider value={navigation}>
            {/* In-process overlay host so a gate stage (e.g. create-workspace) can
              open a modal/drawer; no respond channel is wired (service-side
              overlays stay in the main window, §5.8). */}
            <OverlayProvider
              client={client}
              views={views}
              registry={registry}
              moduleService={moduleService}
              presentWindow={
                windowControlsBridge
                  ? () => windowControlsBridge.present()
                  : undefined
              }
            >
              <div className="flex h-full w-full flex-col overflow-hidden bg-card font-[system-ui,sans-serif] text-foreground">
                {/* Same frameless chrome as the workbench (§5.8): drag region +
                  window controls, so the window stays movable/closable pre-login. */}
                <CustomHeader bridge={windowControlsBridge} />
                <div className="flex min-h-0 flex-1">
                  {view ? (
                    <SurfaceView
                      viewId={view.id}
                      moduleId={view.moduleId}
                      registry={registry}
                      moduleService={moduleService}
                      kind={view.kind}
                      url={view.url}
                      iframeBridge={iframeBridge}
                      overlayParams={gate.params}
                      subscribeContributionsChanged={
                        client
                          ? (listener) =>
                              client.listen(
                                CONTRIBUTIONS_CHANGED_EVENT,
                                listener,
                              )
                          : undefined
                      }
                    />
                  ) : null}
                </div>
              </div>
            </OverlayProvider>
          </NavigationContext.Provider>
        </ViewEventBusContext.Provider>
      </UiClientConfigProvider>
    </SurfaceHostedInProvider>
  );
}
